/**
 * CardDetailDrawer — slide-over panel for viewing + editing a single
 * Kanban card (title, description, dates, status, priority, lane, audit
 * log, linked entities, archive/restore).
 *
 * This file is a THIN ORCHESTRATOR. The 2,800-line monolith was split
 * (operator 500-line ceiling) into pure, verbatim modules under
 * `card-detail/` — behaviour is unchanged:
 *   - usePinnedDrawer / drawerChrome — pinned state + presentation primitives
 *   - useCardDrawerData    — full-card refetch, similar/schedule queries,
 *                            cache writers, lane/actor lookups
 *   - useCardDrawerEditing — local edit state + the save pipeline
 *   - useCardDrawerLayout  — viewport/pin/width/resize + CSS-var publishing
 *   - drawer-sections/*    — header, rail tab strip, tab bar, the three tab
 *                            panes (card/email/files), footer
 *
 * Backend: every patch hits `PATCH /api/kanban/cards/<id>/`. Lane moves use
 * `POST /api/kanban/cards/<id>/move/` so the server appends an
 * `audit_log_json` entry with `event=lane_changed`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, FileText, GitMerge, Mail, Paperclip } from 'lucide-react'

import { useInPageFind } from '../../components/find/useInPageFind'
import { setMeetingContextCard } from '../meetingSession'
import { cardLooksLikeEmail } from './EmailFromCard'
import { KanbanMergeCardsDialog } from './KanbanMergeCardsDialog'
import type { Card } from '../types'
import { orderLinkedEmailIds } from './card-detail/linkedEmails'
import {
  DEFAULT_WIDTH_OVERLAY_PX,
  DEFAULT_WIDTH_PINNED_PX,
  MAX_DRAWER_WIDTH_PX,
  MIN_DRAWER_WIDTH_PX,
  type CardDetailTab,
} from './card-detail/drawerChrome'
import type { CardDetailDrawerProps } from './card-detail/cardDetailDrawerProps'
import { useCloseDrawerOnOutsideClick, useKanbanOpenMode } from './card-detail/openModePref'
import { shouldDismissContextMenu } from '../hooks/shouldDismissContextMenu'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { useCardDrawerData } from './card-detail/useCardDrawerData'
import { useCardDrawerEditing } from './card-detail/useCardDrawerEditing'
import { useCardDrawerLayout } from './card-detail/useCardDrawerLayout'
import { DrawerRailTabStrip } from './card-detail/drawer-sections/DrawerRailTabStrip'
import { DrawerHeader } from './card-detail/drawer-sections/DrawerHeader'
import { DrawerTabBar, type DrawerTab } from './card-detail/drawer-sections/DrawerTabBar'
import { EmailTabPane } from './card-detail/drawer-sections/EmailTabPane'
import { FilesTabPane } from './card-detail/drawer-sections/FilesTabPane'
import { CardTabContent } from './card-detail/drawer-sections/CardTabContent'
import { DrawerFooter } from './card-detail/drawer-sections/DrawerFooter'
import { useDrawerFocusFromDeepLink } from './card-detail/useDrawerFocusFromDeepLink'
import { RightRailPhoneSheetHandle } from './RightRailPhoneSheetHandle'

// Back-compat re-exports — index.ts and other components import these from
// './CardDetailDrawer'.
export { _humaniseAuditEvent, ActivityRow, type ActivityRowProps } from './card-detail/activity'
export { CascadeArchiveModal } from './card-detail/CascadeArchiveModal'
export type { CardDetailTab } from './card-detail/drawerChrome'
export type { CardDetailDrawerProps } from './card-detail/cardDetailDrawerProps'

export function CardDetailDrawer(props: CardDetailDrawerProps) {
  const {
    open,
    card: cardProp,
    // Default-empty so a host that mounts the drawer without board lanes
    // (PlannerPage's cross-board mode) can't crash `lanes.find` at render.
    lanes = [],
    api,
    boardId,
    onChanged,
    onClose,
    renderLinks,
    members,
    tags,
    onTagCreated,
    orgId,
    emailAdapter,
    boardCards,
    currentUserId,
    cashFlowEnabled,
    inlineMode = false,
    widthOverride,
    railActiveTab,
    onRailTabChange,
    onInlineResize,
    onInlineResizeEnd,
    onInlineResizeReset,
    inlineActive = true,
    onSelectCards,
    onConvertToNotebook,
    onOpenCardId,
    onOpenEntryCardId,
    onOpenWorkbook,
    onSwitchBoard,
    linkNavDepth = 0,
    onBack,
    initialFocusSection,
    initialFocusEntityId,
  } = props

  // Read-side state (queries, cache writers, lookups). `data.card` is the
  // full card (lean prop until the refetch resolves).
  const data = useCardDrawerData({ api, cardProp, open, lanes, members, onChanged })
  const card = data.card

  // Local edit state + the save pipeline.
  const editing = useCardDrawerEditing({
    api,
    card,
    members,
    open,
    onClose,
    pushFocusedCardUpdate: data.pushFocusedCardUpdate,
  })

  // FEAT card-context signal (D2 2026-07-06) — while this drawer is open
  // during a live meeting, the card becomes the ACTIVE context: speech
  // segments carry its id and unresolved AI actions attach to it. No-op
  // when no meeting is live (the session only reads it at ingest time).
  useEffect(() => {
    if (!open || !card?.id) return
    setMeetingContextCard(card.id)
    return () => setMeetingContextCard(null)
  }, [open, card?.id])

  // ARC-1 MR5 (U14) — per-user open-mode pref (drawer vs Trello popup).
  // Read HERE (not in the host) so every mount of the universal drawer
  // honours it; the toggle lives in the drawer header.
  const [openMode, setOpenMode] = useKanbanOpenMode(currentUserId)
  const [closeOnOutsideClick] = useCloseDrawerOnOutsideClick(currentUserId)

  // Chrome/layout (viewport, pin, width, resize, CSS vars, Esc).
  const layout = useCardDrawerLayout({
    open,
    card,
    inlineMode,
    inlineActive,
    widthOverride,
    onInlineResize,
    onInlineResizeEnd,
    closeWithFlush: editing.closeWithFlush,
    openMode,
  })

  // Focus trap — only for the true modal variants (overlay / popup). The
  // pinned rail is a non-modal `complementary` and the inline rail must keep
  // the board reachable, so neither traps. Escape-to-close stays in
  // useCardDrawerLayout (it owns the esc typing-guard pin).
  const dialogTrapRef = useRef<HTMLDivElement | null>(null)
  useFocusTrap(dialogTrapRef, {
    active: open && !!card && !layout.pinned && !inlineMode,
  })

  // Outside click closes the split pane and the overlay. Default on.
  // Settings → Kanban can turn it off. Nested popovers (date picker)
  // stay open because shouldDismissContextMenu treats them as inside.
  useEffect(() => {
    if (!open || !card || !closeOnOutsideClick) return
    if (inlineMode && !inlineActive) return
    const onPointerDown = (event: PointerEvent) => {
      if (!shouldDismissContextMenu(dialogTrapRef.current, event.target)) return
      void editing.closeWithFlush()
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [
    open,
    card,
    closeOnOutsideClick,
    inlineMode,
    inlineActive,
    editing.closeWithFlush,
  ])

  // `isEmailShaped` — a Trello / BenkoBoard import whose title is an email
  // header. Drives the Email tab's synthesised reader + keeps the tab visible.
  const isEmailShaped = card !== null && cardLooksLikeEmail({
    primary_email: (card.primary_email ?? null) as string | null,
    title: card.title,
    summary: card.summary,
  })

  // Always open on the Card tab; the linked email renders inline above the
  // description, so there's no reason to bury it behind the Email tab.
  const [activeTab, setActiveTab] = useState<CardDetailTab>('card')
  // Email id to pre-select when opening the Email tab via "Open full email"
  // on a specific linked row.
  const [emailTabInitialId, setEmailTabInitialId] = useState<string | null>(null)
  const openEmailTab = useCallback((emailId?: string) => {
    setEmailTabInitialId(emailId ?? null)
    setActiveTab('email')
  }, [])
  // Re-pick the default tab when the operator switches cards (tracked by id so
  // a save-induced re-render doesn't yank them off the tab they're reading).
  useEffect(() => {
    setActiveTab('card')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [card?.id])

  // Mention deep-link: keep Card tab selected + scroll to Team Chat / notes /
  // description (and optionally a row). Idempotent per (card, focus, id).
  const ensureCardTab = useCallback(() => {
    setActiveTab('card')
    onRailTabChange?.('card')
  }, [onRailTabChange])
  useDrawerFocusFromDeepLink({
    open,
    cardId: card?.id,
    focus: initialFocusSection,
    entityId: initialFocusEntityId,
    ensureCardTab,
  })

  // ⌘F find-in-card — scoped to the open card's body. Gated to the Card tab.
  const cardBodyFindRef = useRef<HTMLDivElement | null>(null)
  const cardFind = useInPageFind({
    containerRef: cardBodyFindRef,
    id: 'card-detail',
    label: 'card',
    enabled: open && !!card && activeTab === 'card',
  })

  // Every email linked to this card — primary first, then related, deduped.
  // Memoised before the early return so the hook order stays stable.
  const emailLinkedIds = useMemo(
    () => orderLinkedEmailIds(card?.primary_email, card?.related_emails),
    [card?.primary_email, card?.related_emails],
  )

  if (!open || !card) return null

  // The card whose collaboration we read/write: the parent for a mirror
  // (once loaded), else the card itself. Schedule/status stay on `card`.
  const collabCard: Card = data.mirrorParentId ? (data.parentQ.data ?? card) : card
  const currentLane = lanes.find((l) => l.id === card.lane)
  // Audit log — most-recent first by `at` (not append order alone), so
  // concurrent writers still render in wall-clock sequence.
  const allLog = (card.audit_log_json ?? [])
    .slice()
    .sort((a, b) => String(a.at ?? '').localeCompare(String(b.at ?? '')))
    .reverse()

  // Tab visibility — Email tab shows when the card has linked emails + an
  // adapter, is email-shaped, or the host wired an adapter at all.
  const hasEmail = (emailLinkedIds.length > 0 && !!emailAdapter) || isEmailShaped
  const showEmailTab = hasEmail || !!emailAdapter
  const tabs: DrawerTab[] = [
    { id: 'card',  label: 'Card',  icon: <FileText className="h-3.5 w-3.5" />, visible: true },
    { id: 'email', label: 'Email', icon: <Mail className="h-3.5 w-3.5" />, visible: showEmailTab },
    { id: 'files', label: 'Files', icon: <Paperclip className="h-3.5 w-3.5" />, visible: true },
  ]
  const visibleTabs = tabs.filter((t) => t.visible)
  const safeActiveTab: CardDetailTab = visibleTabs.some((t) => t.id === activeTab)
    ? activeTab
    : 'card'

  const shell = (
    <div
      ref={dialogTrapRef}
      role={layout.pinned ? 'complementary' : 'dialog'}
      aria-modal={layout.pinned ? undefined : true}
      aria-label={layout.pinned ? 'Card detail rail' : undefined}
      // BUG-2026-06-16-KANBAN-CARD-RAIL-FLICK — keep-alive: hidden in place on
      // the Emails/Jobs tabs, so mark it aria-hidden to AT.
      aria-hidden={inlineMode && !inlineActive ? true : undefined}
      className={layout.wrapperClass}
      style={layout.wrapperStyle}
      onClick={(e) => {
        // Backdrop-click-to-close only applies to the overlay variant.
        // closeWithFlush so an unsaved title/summary lands before tear-down.
        if (
          closeOnOutsideClick
          && !layout.pinned
          && e.target === e.currentTarget
        ) void editing.closeWithFlush()
      }}
    >
      <aside className={layout.asideClass} style={layout.asideStyle}>
        {inlineMode && (
          <RightRailPhoneSheetHandle
            closeAriaLabel="Close card sheet"
            onClose={() => void editing.closeWithFlush()}
          />
        )}
        {/* Resize handle (legacy overlay/pinned modes). Drives the drawer's
            own local `drawerWidth`. A popup has a fixed centered width. */}
        {!inlineMode && !layout.popup && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize card drawer"
            aria-valuemin={MIN_DRAWER_WIDTH_PX}
            aria-valuemax={MAX_DRAWER_WIDTH_PX}
            aria-valuenow={layout.drawerWidth}
            onPointerDown={layout.onResizeStart}
            onDoubleClick={() => layout.setDrawerWidth(layout.pinned ? DEFAULT_WIDTH_PINNED_PX : DEFAULT_WIDTH_OVERLAY_PX)}
            title="Drag to resize · double-click to reset"
            // `touch-none` + the coarse-pointer widening are what make this
            // pointer handler actually reachable on a tablet: without the
            // former the browser claims the gesture for panning before the
            // first pointermove lands, and 6px is under half a fingertip.
            className="absolute inset-y-0 left-0 z-20 w-1.5 touch-none cursor-col-resize bg-transparent hover:bg-blue-400/60 active:bg-blue-500/80 transition-colors [@media(pointer:coarse)]:w-3"
          />
        )}
        {/* FEAT-2026-06-07-KANBAN-DRAWER-RESIZE — inline-mode resize handle
            (md+ only; on phone the rail is a full-width bottom sheet). */}
        {inlineMode && onInlineResize && !layout.isNarrowViewport && !layout.popup && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize card drawer"
            onPointerDown={layout.onInlineResizeStart}
            onDoubleClick={() => onInlineResizeReset?.()}
            title="Drag to resize · double-click to reset"
            // Inline mode is the one a tablet actually sees (md+ renders the
            // rail as a flex sibling), so it needs the touch affordances most.
            className="absolute inset-y-0 left-0 z-30 w-1.5 touch-none cursor-col-resize bg-transparent hover:bg-blue-400/60 active:bg-blue-500/80 transition-colors [@media(pointer:coarse)]:w-3"
          />
        )}
        {/* Inline-mode rail tab strip — mirrors the Emails / Jobs / Card
            segmented control the email + jobs rails render. */}
        {inlineMode && railActiveTab && onRailTabChange && !layout.popup && (
          <DrawerRailTabStrip railActiveTab={railActiveTab} onRailTabChange={onRailTabChange} />
        )}
        {/* Header — title is inline-editable */}
        <DrawerHeader
          card={card}
          currentLane={currentLane}
          inlineMode={inlineMode}
          api={api}
          boardCards={boardCards}
          editing={editing}
          data={data}
          layout={layout}
          openMode={openMode}
          setOpenMode={setOpenMode}
        />

        {/* FEAT-2026-07-07-KANBAN-MERGE-OPEN-BACK — when the operator reached
            this card by following a merge/linked deep link in the Activity
            trail, mark it CLEARLY (a solid amber "Linked card" chip, a depth
            counter when >1 deep, and a stronger left accent + tinted bar so
            it's obvious this is NOT the card they opened) and offer a Back
            control that returns to the previous card in the chain (the parent
            pops one breadcrumb level rather than blanking the slot). Only
            shown when a breadcrumb exists. */}
        {linkNavDepth > 0 && onBack && (
          <div className="flex items-center justify-between gap-2 border-b border-l-4 border-amber-300 border-l-amber-500 bg-amber-100/70 px-4 py-2 dark:border-amber-500/40 dark:border-l-amber-500 dark:bg-amber-950/40">
            <span className="inline-flex min-w-0 items-center gap-2">
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white dark:bg-amber-600">
                <GitMerge className="h-3 w-3" aria-hidden />
                Linked card
              </span>
              <span className="truncate text-[11px] font-medium text-amber-900 dark:text-amber-200">
                Opened from activity
                {linkNavDepth > 1 ? ` · ${linkNavDepth} deep` : ''}
              </span>
            </span>
            <button
              type="button"
              onClick={onBack}
              className="inline-flex shrink-0 items-center gap-1 rounded border border-amber-400/60 bg-white/70 px-2 py-0.5 text-[11px] font-semibold text-amber-900 transition hover:bg-white dark:border-amber-500/40 dark:bg-amber-900/40 dark:text-amber-100 dark:hover:bg-amber-900/70"
              title="Return to the previous card in the chain"
              aria-label="Back to previous card"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
              Back to previous card
            </button>
          </div>
        )}

        {editing.error && (
          <div className="border-b border-red-200 bg-red-50 dark:bg-red-950/30 px-4 py-2 text-xs text-red-700 dark:text-red-300">
            {editing.error}
          </div>
        )}

        {/* Tab strip — hidden when only the Card tab is visible. */}
        {visibleTabs.length > 1 && (
          <DrawerTabBar visibleTabs={visibleTabs} safeActiveTab={safeActiveTab} setActiveTab={setActiveTab} />
        )}

        {/* Email tab pane — only mounted while active so the adapter fetch
            fires lazily on tab open. */}
        {safeActiveTab === 'email' && showEmailTab && (
          <EmailTabPane
            card={card}
            api={api}
            emailAdapter={emailAdapter}
            emailLinkedIds={emailLinkedIds}
            isEmailShaped={isEmailShaped}
            emailTabInitialId={emailTabInitialId}
            onRailTabChange={onRailTabChange}
          />
        )}

        {/* Files tab pane. */}
        {safeActiveTab === 'files' && (
          <FilesTabPane card={card} api={api} emailAdapter={emailAdapter} />
        )}

        {/* Card tab pane — always mounted, hidden in place on other tabs. */}
        <CardTabContent
          card={card}
          collabCard={collabCard}
          lanes={lanes}
          members={members}
          tags={tags}
          api={api}
          boardId={boardId}
          boardCards={boardCards}
          orgId={orgId}
          cashFlowEnabled={cashFlowEnabled}
          emailAdapter={emailAdapter}
          currentUserId={currentUserId}
          renderLinks={renderLinks}
          onChanged={onChanged}
          onTagCreated={onTagCreated}
          onSelectCards={onSelectCards}
          onConvertToNotebook={onConvertToNotebook}
          onOpenCardId={onOpenCardId}
          onOpenEntryCardId={onOpenEntryCardId}
          onOpenWorkbook={onOpenWorkbook}
          // Navigating to another of the card's boards closes this drawer
          // (the card detail belongs to the board being left) and hands the
          // route change to the host. Disabled affordance when unwired.
          onOpenBoard={
            onSwitchBoard
              ? (targetBoardId) => {
                  onSwitchBoard(targetBoardId)
                  onClose()
                }
              : undefined
          }
          allLog={allLog}
          safeActiveTab={safeActiveTab}
          setActiveTab={setActiveTab}
          openEmailTab={openEmailTab}
          cardBodyFindRef={cardBodyFindRef}
          findBarProps={cardFind.bar}
          editing={editing}
          data={data}
        />

        {/* Footer — destructive actions */}
        <DrawerFooter card={card} editing={editing} />
      </aside>

      {/* Merge dialog (item 13) — sibling of the aside so its backdrop covers
          both the drawer and the kanban behind it. */}
      {api.mergeCards && boardCards && (
        <KanbanMergeCardsDialog
          open={data.mergeDialogOpen}
          keeper={card}
          candidates={boardCards}
          api={api}
          preselectLoserIds={data.mergePreselectId ? [data.mergePreselectId] : undefined}
          onClose={() => {
            data.setMergeDialogOpen(false)
            data.setMergePreselectId(null)
          }}
          onMerged={(_mergedIds, keeper) => {
            data.setMergePreselectId(null)
            // Fix #4: push the FRESH keeper returned by the merge endpoint —
            // it carries the new `merged_thread_into_card` audit row (with
            // `merged_card_id`), so the activity feed renders the "Open
            // merged-in card" + "Undo merge" affordances immediately instead
            // of only after a background refetch. Fall back to the stale
            // `card` if a pre-2026-07-06 host omits the payload.
            data.pushFocusedCardUpdate(keeper ?? card)
          }}
        />
      )}
    </div>
  )
  // Phone + inline: Job Hub (and any overflow-hidden host) clips an
  // in-tree absolute sheet. Portal the overlay to <body>.
  if (layout.escapeToViewport && typeof document !== 'undefined') {
    return createPortal(shell, document.body)
  }
  return shell
}
